import React, { useEffect, useRef, useState } from "react";
import classNames from 'classnames';

interface VerificationCodeProps {
    length?: number;
    onChange: (otp: string) => void;
    error?: string | null;
}

export default function VerificationCode({ length = 4, onChange, error }: VerificationCodeProps) {
    const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        onChange(otp.join(""));
    }, [otp, onChange]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        const { key } = e;

        if (/^[0-9]$/.test(key)) {
            e.preventDefault();

            setOtp((prevOtp) => {
                const newOtp = [...prevOtp];
                newOtp[index] = key;
                return newOtp;
            });

            if (index < otp.length - 1) {
                setTimeout(() => {
                    inputRefs.current[index + 1]?.focus();
                }, 0);
            } else {
                setTimeout(() => {
                    inputRefs.current[index]?.blur();
                }, 0);
            }

            return;
        }

        if (key === "Backspace") {
            e.preventDefault();
            if (otp[index] === "") {
                if (index > 0) {
                    setOtp((prevOtp) => {
                        const newOtp = [...prevOtp];
                        newOtp[index - 1] = "";
                        return newOtp;
                    });
                    setTimeout(() => {
                        inputRefs.current[index - 1]?.focus();
                    }, 0);
                }
            } else {
                setOtp((prevOtp) => {
                    const newOtp = [...prevOtp];
                    newOtp[index] = "";
                    return newOtp;
                });
                setTimeout(() => {
                    inputRefs.current[index]?.focus();
                }, 0);
            }
            return;
        }

        if (key === "Delete") {
            e.preventDefault();
            if (otp[index] !== "") {
                setOtp((prevOtp) => {
                    const newOtp = [...prevOtp];
                    newOtp[index] = "";
                    return newOtp;
                });
                if (index < otp.length - 1) {
                    setTimeout(() => {
                        inputRefs.current[index + 1]?.focus();
                    }, 0);
                }
            }
            return;
        }

        if (key !== "Tab" && !e.metaKey) {
            e.preventDefault();
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;
        if (/^[0-9]{1}$/.test(value)) {
            setOtp((prevOtp) => {
                const newOtp = [...prevOtp];
                newOtp[index] = value;
                return newOtp;
            });
        }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        if (inputRefs.current[index]) {
            inputRefs.current[index]?.focus();
            inputRefs.current[index]?.select();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text");
        const digits = text.replace(/\D/g, "").slice(0, otp.length - index).split("");
        if (digits.length === 0) return;

        setOtp((prevOtp) => {
            const newOtp = [...prevOtp];
            digits.forEach((digit, idx) => {
                newOtp[index + idx] = digit;
            });
            return newOtp;
        });

        setTimeout(() => {
            const nextIndex = index + digits.length < otp.length ? index + digits.length : otp.length - 1;
            inputRefs.current[nextIndex]?.focus();
            if (nextIndex === otp.length - 1) {
                inputRefs.current[nextIndex]?.blur();
            }
        }, 0);
    };

    const handleClick = (index: number) => {
        if (inputRefs.current[index]) {
            inputRefs.current[index]?.focus();
            inputRefs.current[index]?.select();
        }
    };

    return (
      <div>
          <div id="otp-form" className="flex gap-2.5">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInput(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  onFocus={handleFocus}
                  onPaste={(e) => handlePaste(e, index)}
                  onClick={() => handleClick(index)}
                  onMouseDown={(e) => handleMouseDown(e, index)}
                  ref={(el) => (inputRefs.current[index] = el)}
                  className={classNames(
                    'flex w-10 h-10 xxs:w-12 xxs:h-12 items-center justify-center rounded-[8px] border bg-white p-2 text-center text-base/[1.3] placeholder:text-[#A5A5A5] outline-none ',
                    error ? 'border-[#FF825B]' : 'border-[#DBDBDB] focus:border-[#3CBBCE]',
                  )}
                  aria-label={`OTP Digit ${index + 1}`}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              ))}
          </div>
          {error && (
            <div className="text-sm text-red-orange mt-2 font-medium">
                {error}
            </div>
          )}
      </div>
    );
}
